import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (number: number) => void;
  onItemsPerPageChange: (number: number) => void;
  showItemsPerPage?: boolean;
}

export const PaginationControls = ({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
}: PaginationControlsProps) => {
  const generatePageNumbers = () => {
    const totalPages_local = totalPages;
    const currentPage_local = currentPage;

    if (totalPages_local <= 7) {
      return Array.from({ length: totalPages_local }, (_, i) => i + 1);
    }

    if (currentPage_local <= 4) {
      return [...Array.from({ length: 5 }, (_, i) => i + 1), 'ellipsis', totalPages_local];
    }

    if (currentPage_local >= totalPages_local - 3) {
      return [1, 'ellipsis', ...Array.from({ length: 5 }, (_, i) => totalPages_local - 4 + i)];
    }

    return [
      1,
      'ellipsis',
      currentPage_local - 1,
      currentPage_local,
      currentPage_local + 1,
      'ellipsis',
      totalPages_local,
    ];
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="mb-8">
      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
        {showItemsPerPage && (
          <div className="flex items-center gap-3">
            <span className="text-memorial-muted text-sm">Show:</span>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(value) => onItemsPerPageChange(Number.parseInt(value, 10))}
            >
              <SelectTrigger className="bg-memorial-card border-memorial-border text-memorial-text w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-memorial-card border-memorial-border z-50">
                <SelectItem value="8" className="text-memorial-text hover:bg-memorial-bg">
                  8
                </SelectItem>
                <SelectItem value="12" className="text-memorial-text hover:bg-memorial-bg">
                  12
                </SelectItem>
                <SelectItem value="24" className="text-memorial-text hover:bg-memorial-bg">
                  24
                </SelectItem>
                <SelectItem value="48" className="text-memorial-text hover:bg-memorial-bg">
                  48
                </SelectItem>
                <SelectItem value="96" className="text-memorial-text hover:bg-memorial-bg">
                  96
                </SelectItem>
              </SelectContent>
            </Select>
            {/*<span className="text-memorial-muted text-sm">per page</span>*/}
          </div>
        )}

        {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) onPageChange(currentPage - 1);
                  }}
                  className={cn(
                    'mr-2',
                    currentPage === 1
                      ? 'pointer-events-none opacity-50'
                      : 'text-memorial-text hover:text-cyber-glow',
                  )}
                />
              </PaginationItem>

              {generatePageNumbers().map((page) => (
                <PaginationItem key={`page-${page}`}>
                  {page === 'ellipsis' ? (
                    <PaginationEllipsis className="text-memorial-muted" />
                  ) : (
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        onPageChange(page as number);
                      }}
                      isActive={currentPage === page}
                      className={
                        currentPage === page
                          ? 'bg-cyber-glow text-primary-foreground hover:bg-cyber-glow/80'
                          : 'text-memorial-text hover:text-cyber-glow hover:bg-memorial-bg'
                      }
                    >
                      {page}
                    </PaginationLink>
                  )}
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage < totalPages) onPageChange(currentPage + 1);
                  }}
                  className={cn(
                    'ml-2',
                    currentPage === totalPages
                      ? 'pointer-events-none opacity-50'
                      : 'text-memorial-text hover:text-cyber-glow',
                  )}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>

      <div className="text-memorial-muted mt-16 text-center text-sm sm:text-left">
        Showing {startItem} to {endItem} of {totalItems} software
      </div>
    </div>
  );
};
